import {
  ArrowRight,
  Bookmark,
  BookmarkX,
  Dices,
  FolderHeart,
  Play,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import type { Game } from "../../data/games";
import { GameArtwork } from "../GameArtwork/GameArtwork";
import { SubpageHeader } from "../SubpageHeader/SubpageHeader";
import styles from "./SavedGamesView.module.css";

interface SavedGamesViewProps {
  games: readonly Game[];
  onBack: () => void;
  onBrowseGames: () => void;
  onMenuOpen: (trigger: HTMLButtonElement) => void;
  onRemove: (game: Game) => void;
  onSelectGame: (game: Game, trigger: HTMLButtonElement) => void;
}

export function SavedGamesView({
  games,
  onBack,
  onBrowseGames,
  onMenuOpen,
  onRemove,
  onSelectGame,
}: SavedGamesViewProps) {
  const firstGame = games[0];

  return (
    <>
      <SubpageHeader
        eyebrow="Din samling"
        title="Sparade spel"
        onBack={onBack}
        onMenuOpen={onMenuOpen}
      />

      <main className={styles.view}>
        <section className={styles.hero} aria-labelledby="saved-heading">
          <div className={styles.heroOrb} aria-hidden="true" />
          <div className={styles.heroIcon}>
            <FolderHeart aria-hidden="true" />
            <span>{games.length}</span>
          </div>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>
              <Sparkles aria-hidden="true" />
              Redo när ni är
            </span>
            <h1 id="saved-heading">Ert eget spelvalv</h1>
            <p>
              Favoriterna samlade på ett ställe, redo för nästa spontana
              spelkväll.
            </p>
          </div>
          {firstGame ? (
            <button
              className={styles.surpriseButton}
              type="button"
              onClick={(event) => onSelectGame(firstGame, event.currentTarget)}
            >
              <Dices aria-hidden="true" />
              Överraska oss
              <ArrowRight aria-hidden="true" />
            </button>
          ) : null}
        </section>

        {games.length > 0 ? (
          <section
            className={styles.collection}
            aria-labelledby="collection-heading"
          >
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>Sparat lokalt</span>
                <h2 id="collection-heading">Kvällens kortlek</h2>
              </div>
              <span className={styles.count}>{games.length} spel</span>
            </div>

            <div className={styles.gameList}>
              {games.map((game, index) => (
                <article className={styles.gameCard} key={game.id}>
                  <button
                    className={styles.artworkButton}
                    type="button"
                    aria-label={`Öppna detaljer för ${game.title}`}
                    onClick={(event) =>
                      onSelectGame(game, event.currentTarget)
                    }
                  >
                    <GameArtwork
                      imageUrl={game.imageUrl}
                      title={game.title}
                      fallbackLabel={game.fallbackLabel}
                      categoryId={game.categoryId}
                      priority={index === 0}
                    />
                    <span className={styles.playMark}>
                      <Play aria-hidden="true" fill="currentColor" />
                    </span>
                  </button>

                  <div className={styles.cardBody}>
                    <div className={styles.cardMeta}>
                      <span>{game.category}</span>
                      <span>
                        <Star aria-hidden="true" />
                        {game.rating.toFixed(1)}
                      </span>
                    </div>
                    <h3>{game.title}</h3>
                    <p>{game.description}</p>
                    <div className={styles.cardFooter}>
                      <span>
                        <Users aria-hidden="true" />
                        {game.minPlayers}–{game.maxPlayers} spelare
                      </span>
                      <button
                        type="button"
                        aria-label={`Ta bort ${game.title} från sparade spel`}
                        onClick={() => onRemove(game)}
                      >
                        <BookmarkX aria-hidden="true" />
                        Ta bort
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : (
          <section className={styles.emptyState} aria-labelledby="empty-heading">
            <span className={styles.emptyIcon}>
              <Bookmark aria-hidden="true" />
            </span>
            <span className={styles.eyebrow}>Valvet väntar</span>
            <h2 id="empty-heading">Inga sparade spel ännu</h2>
            <p>
              Utforska spelvalvet och samla favoriterna som alltid får igång
              gänget.
            </p>
            <button type="button" onClick={onBrowseGames}>
              Hitta ett spel
              <ArrowRight aria-hidden="true" />
            </button>
          </section>
        )}
      </main>
    </>
  );
}
